import { useState } from 'react';
import { Send, Phone, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';

const BookingSection = () => {
  const { language } = useLanguage();
  const t = translations[language].booking;
  
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });

  const serviceOptions = language === 'hu' ? [
    'Elektromos szerelés',
    'Vízszerelés, fűtés',
    'Konyha & bútorok',
    'Lakásfelújítás',
    'Kertgondozás',
    'Infotechnika & IT'
  ] : [
    'Electrical Services',
    'Plumbing & Heating',
    'Kitchen & Furniture',
    'Home Renovation',
    'Gardening',
    'IT Services'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Call backend API to send emails
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, language })
      });

      if (!response.ok) {
        throw new Error('Failed to send booking request');
      }

      // Show success message
      setSubmitted(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', phone: '', email: '', service: '', message: '' });
      }, 3000);
    } catch (error) {
      console.error('Error sending booking:', error);
      alert('Hiba történt az üzenet küldése során. Kérjük, próbálja újra vagy hívjon minket közvetlenül.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="idopontfoglalas" className="py-24 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="text-[#7B2F3D] font-semibold text-sm uppercase tracking-widest">{t.badge}</span>
          <h2 className="text-4xl font-bold mt-3 text-gray-900">
            {t.title}
          </h2>
          <p className="text-gray-600 mt-4">
            {t.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#7B2F3D] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide">{t.contactCards.handyman}</p>
                  <a href="tel:+36702378089" className="text-xl font-bold text-[#7B2F3D] hover:text-[#8B3A3A]">
                    06/70-237-8089
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#7B2F3D] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide">{t.contactCards.it}</p>
                  <a href="tel:+36702701639" className="text-xl font-bold text-[#7B2F3D] hover:text-[#8B3A3A]">
                    06/70-270-1639
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <div>
            {submitted ? (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <Send className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">{t.success.title}</h3>
                <p className="text-gray-600 mt-2">{t.success.message}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 space-y-5">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{t.form.title}</h3>
                <p className="text-sm text-gray-600 mb-6">{t.form.subtitle}</p>
                
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">{t.form.name} {t.form.required}</label>
                    <input 
                      required 
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t.form.namePlaceholder}
                      className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7B2F3D] focus:border-transparent" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">{t.form.phone} {t.form.required}</label>
                    <input 
                      required 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t.form.phonePlaceholder}
                      className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7B2F3D] focus:border-transparent" 
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">{t.form.email}</label>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t.form.emailPlaceholder}
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7B2F3D] focus:border-transparent" 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">{t.form.service} {t.form.required}</label>
                  <select 
                    required 
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7B2F3D] focus:border-transparent"
                  >
                    <option value="">{t.form.servicePlaceholder}</option>
                    {serviceOptions.map((service) => (
                      <option key={service} value={service}>{service}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">{t.form.message}</label>
                  <textarea 
                    rows={4} 
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t.form.messagePlaceholder}
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7B2F3D] focus:border-transparent resize-none" 
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="w-full py-3.5 rounded-lg bg-[#7B2F3D] text-white font-bold hover:bg-[#6A2835] transition-colors flex items-center justify-center gap-2"
                >
                  <Send className="w-5 h-5" />
                  {t.form.submit}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
